import { type PagyMeta, PagyPagination } from "@/components/pagy-pagination"
import { Badge } from "@/components/ui/badge"
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Input } from "@/components/ui/input"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import AppLayout from "@/layouts/AppLayout"
import { Head, router, usePage } from "@inertiajs/react"
import {
  IconDots,
  IconPencil,
  IconPlus,
  IconSearch,
  IconToggleLeft,
  IconToggleRight,
  IconTrash,
} from "@tabler/icons-react"
import { useState } from "react"

interface FeatureFlag {
  id: number
  key: string
  description: string | null
  enabled: boolean
  updated_at: string
}

interface StatusCounts {
  all: number
  enabled: number
  disabled: number
}

interface Props {
  [key: string]: unknown
  flags: FeatureFlag[]
  pagy: PagyMeta
  status_counts: StatusCounts
  filters: { search?: string; enabled?: string }
}

function formatDateTime(dateStr: string) {
  const date = new Date(dateStr)
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
}

export default function AdminFeatureFlagsIndex() {
  const { flags, pagy, status_counts, filters } = usePage<Props>().props

  const [search, setSearch] = useState(filters.search || "")

  function handleSearch(e: React.FormEvent) {
    e.preventDefault()
    router.get(
      "/admin/feature_flags",
      {
        search: search || undefined,
        enabled: filters.enabled || undefined,
      },
      { preserveState: true },
    )
  }

  function handleFilter(enabled?: string) {
    router.get(
      "/admin/feature_flags",
      {
        search: filters.search || undefined,
        enabled,
      },
      { preserveState: true },
    )
  }

  function handleToggle(id: number) {
    router.patch(`/admin/feature_flags/${id}/toggle`)
  }

  function handleDelete(id: number) {
    if (confirm("Are you sure you want to delete this feature flag?")) {
      router.delete(`/admin/feature_flags/${id}`)
    }
  }

  return (
    <>
      <Head title="Feature Flags - Operations Center" />
      <div className="flex flex-1 flex-col gap-4 p-4 lg:p-6">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/">Home</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Feature Flags</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold">Feature Flags</h1>
          <Button size="sm" asChild>
            <a href="/admin/feature_flags/new">
              <IconPlus className="mr-1 h-4 w-4" />
              New Flag
            </a>
          </Button>
        </div>

        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <form onSubmit={handleSearch} className="flex gap-2 sm:max-w-sm">
            <div className="relative flex-1">
              <IconSearch className="text-muted-foreground absolute top-1/2 left-2.5 h-4 w-4 -translate-y-1/2" />
              <Input
                placeholder="Search flags..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <Button type="submit" variant="outline">
              Search
            </Button>
          </form>

          <div className="flex items-center gap-2">
            {(
              [
                { label: "All", value: undefined, count: status_counts.all },
                { label: "Enabled", value: "true", count: status_counts.enabled },
                { label: "Disabled", value: "false", count: status_counts.disabled },
              ] as const
            ).map((filter) => (
              <Button
                key={filter.label}
                variant={filters.enabled === filter.value ? "default" : "outline"}
                size="sm"
                onClick={() => handleFilter(filter.value)}
              >
                {filter.label} ({filter.count})
              </Button>
            ))}
          </div>
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Key</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Last Updated</TableHead>
              <TableHead className="w-10">
                <span className="sr-only">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {flags.map((flag) => (
              <TableRow key={flag.id}>
                <TableCell>
                  <code className="bg-muted rounded px-1.5 py-0.5 text-sm">{flag.key}</code>
                </TableCell>
                <TableCell className="text-muted-foreground">{flag.description || "—"}</TableCell>
                <TableCell>
                  <Badge variant={flag.enabled ? "default" : "destructive"}>
                    {flag.enabled ? "Enabled" : "Disabled"}
                  </Badge>
                </TableCell>
                <TableCell className="text-muted-foreground text-sm">{formatDateTime(flag.updated_at)}</TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <IconDots className="h-4 w-4" />
                        <span className="sr-only">Actions</span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleToggle(flag.id)}>
                        {flag.enabled ? (
                          <>
                            <IconToggleLeft className="mr-2 h-4 w-4" />
                            Disable
                          </>
                        ) : (
                          <>
                            <IconToggleRight className="mr-2 h-4 w-4" />
                            Enable
                          </>
                        )}
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <a href={`/admin/feature_flags/${flag.id}/edit`}>
                          <IconPencil className="mr-2 h-4 w-4" />
                          Edit
                        </a>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem variant="destructive" onClick={() => handleDelete(flag.id)}>
                        <IconTrash className="mr-2 h-4 w-4" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
            {flags.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-muted-foreground text-center">
                  No feature flags found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        <PagyPagination pagy={pagy} />
      </div>
    </>
  )
}

AdminFeatureFlagsIndex.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
